import { Download, Search } from "lucide-react";

import { EntryTable } from "@/components/entries/EntryTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, patchMe } from "@/lib/api";
import { displayPunchAction, displayPunchActionLabel, fmtPunchTime, getLastNDaysRange } from "@/lib/time";
import { useRecentActivityViewModel } from "@/pages/dashboard/employee/useRecentActivityViewModel";

export function RecentActivityView() {
  const vm = useRecentActivityViewModel();
  const queryClient = vm.queryClient;
  const {
    user,
    isAdmin,
    canEditPunches,
    managedEditUserIds,
    recentCostCodeFilterSearch,
    setRecentCostCodeFilterSearch,
    recentCostCodeFilterOpen,
    setRecentCostCodeFilterOpen,
    recentCostCodeFilterRef,
    recentCustomerFilterSearch,
    setRecentCustomerFilterSearch,
    recentCustomerFilterOpen,
    setRecentCustomerFilterOpen,
    recentCustomerFilterRef,
    recentPage,
    setRecentPage,
    recentPageSize,
    setRecentPageSize,
    recentFilterCostCode,
    setRecentFilterCostCode,
    recentFilterCustomer,
    setRecentFilterCustomer,
    recentFromDate,
    setRecentFromDate,
    recentToDate,
    setRecentToDate,
    recentSearch,
    setRecentSearch,
    recentCompact,
    setRecentCompact,
    fieldNotesDialogOpen,
    setFieldNotesDialogOpen,
    fieldNotesEntry,
    setFieldNotesEntry,
    fieldNotesText,
    setFieldNotesText,
    exportError,
    setExportError,
    recentFilterCostCodesData,
    recentFilterCustomersData,
    recentCountData,
    myRecentEntries,
    updateFieldNotesMutation,
    handlePersonalExport,
  } = vm;

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
              <Card className="min-w-0">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recent Activity</CardTitle>
                      <CardDescription>Your recent punches from backend reporting. {recentCountData?.count ?? 0} total.</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Compact</span>
                        <Switch
                          checked={recentCompact}
                          onCheckedChange={(checked) => {
                            setRecentCompact(checked);
                            // Persist preference for this user (fire-and-forget, like dark_mode).
                            // The local state update gives instant UI feedback.
                            patchMe({ recent_compact: checked }).catch(() => {});
                          }}
                        />
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={handlePersonalExport}>
                        <Download className="mr-2 size-4" />
                        CSV
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {exportError && (
                    <div className="mb-2 text-xs text-destructive">
                      {exportError}
                    </div>
                  )}
                  <div className="mb-3 flex flex-wrap gap-2">
                    <div className="relative min-w-[200px] flex-[2]">
                      <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
                      <Input className="pl-9" value={recentSearch} onChange={(event) => { setRecentSearch(event.target.value); setRecentPage(0); }} placeholder="Search your punches" />
                    </div>
                    {/* Searchable cost code filter for recent */}
                    <div className="relative min-w-[160px] flex-1" ref={recentCostCodeFilterRef}>
                      <Input
                        value={recentCostCodeFilterOpen ? recentCostCodeFilterSearch : (recentFilterCostCode === "all" ? "" : (recentFilterCostCodesData ?? []).find((cc) => String(cc.id) === recentFilterCostCode)?.cost_code || "")}
                        onChange={(e) => {
                          setRecentCostCodeFilterSearch(e.target.value);
                          if (!recentCostCodeFilterOpen) setRecentCostCodeFilterOpen(true);
                        }}
                        onFocus={() => {
                          setRecentCostCodeFilterSearch("");
                          setRecentCostCodeFilterOpen(true);
                        }}
                        placeholder={recentCostCodeFilterOpen ? "Search cost codes..." : "All cost codes"}
                      />
                      {recentCostCodeFilterOpen && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          <button
                            type="button"
                            className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                            onClick={() => {
                              setRecentFilterCostCode("all");
                              setRecentCostCodeFilterOpen(false);
                              setRecentCostCodeFilterSearch("");
                              setRecentPage(0);
                            }}
                          >
                            All cost codes
                          </button>
                          {(recentFilterCostCodesData ?? []).map((cc) => (
                            <button
                              type="button"
                              key={cc.id}
                              className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                              onClick={() => {
                                setRecentFilterCostCode(String(cc.id));
                                setRecentCostCodeFilterOpen(false);
                                setRecentCostCodeFilterSearch("");
                                setRecentPage(0);
                              }}
                            >
                              {cc.cost_code}
                            </button>
                          ))}
                          {(recentFilterCostCodesData ?? []).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Searchable customer filter for recent */}
                    <div className="relative min-w-[160px] flex-1" ref={recentCustomerFilterRef}>
                      <Input
                        value={recentCustomerFilterOpen ? recentCustomerFilterSearch : (recentFilterCustomer === "all" ? "" : (recentFilterCustomersData ?? []).find((c) => String(c.id) === recentFilterCustomer)?.company || "")}
                        onChange={(e) => {
                          setRecentCustomerFilterSearch(e.target.value);
                          if (!recentCustomerFilterOpen) setRecentCustomerFilterOpen(true);
                        }}
                        onFocus={() => {
                          setRecentCustomerFilterSearch("");
                          setRecentCustomerFilterOpen(true);
                        }}
                        placeholder={recentCustomerFilterOpen ? "Search customers..." : "All customers"}
                      />
                      {recentCustomerFilterOpen && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          <button
                            type="button"
                            className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                            onClick={() => {
                              setRecentFilterCustomer("all");
                              setRecentCustomerFilterOpen(false);
                              setRecentCustomerFilterSearch("");
                              setRecentPage(0);
                            }}
                          >
                            All customers
                          </button>
                          {(recentFilterCustomersData ?? []).map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                              onClick={() => {
                                setRecentFilterCustomer(String(c.id));
                                setRecentCustomerFilterOpen(false);
                                setRecentCustomerFilterSearch("");
                                setRecentPage(0);
                              }}
                            >
                              {c.company}
                            </button>
                          ))}
                          {(recentFilterCustomersData ?? []).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => { setExportError(null); const r = getLastNDaysRange(7); setRecentFromDate(r.from); setRecentToDate(r.to); setRecentPage(0); }}>Week</Button>
                      <Button size="sm" variant="outline" onClick={() => { setExportError(null); const r = getLastNDaysRange(30); setRecentFromDate(r.from); setRecentToDate(r.to); setRecentPage(0); }}>Month</Button>
                    </div>
                    <Input className="min-w-[160px] flex-1" type="date" value={recentFromDate} onChange={(event) => setRecentFromDate(event.target.value)} />
                    <Input className="min-w-[160px] flex-1" type="date" value={recentToDate} onChange={(event) => setRecentToDate(event.target.value)} />
                  </div>
                  <div className="mb-2 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setExportError(null); setRecentSearch(""); setRecentFilterCostCode("all"); setRecentFilterCustomer("all"); setRecentFromDate(""); setRecentToDate(""); setRecentPage(0); setRecentCostCodeFilterSearch(""); setRecentCustomerFilterSearch(""); setRecentCostCodeFilterOpen(false); setRecentCustomerFilterOpen(false); }}>Clear filters</Button>
                  </div>
                  <EntryTable
                    entries={myRecentEntries}
                    compact={recentCompact}
                    currentUserId={user?.id}
                    showQbLink={user?.role !== "subcontractor"}
                    canEditPunches={canEditPunches}
                    editableUserIds={managedEditUserIds}
                    canDeletePunches={!!isAdmin}
                    onEditFieldNotes={(entry) => { setFieldNotesEntry(entry); setFieldNotesText(entry.field_notes || ""); setFieldNotesDialogOpen(true); }}
                    onEntryUpdated={() => {
                      void queryClient.invalidateQueries({ queryKey: ["entries"] });
                      void queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
                      void queryClient.invalidateQueries({ queryKey: ["correction-stats"] });
                    }}
                    onEntryDeleted={() => {
                      void queryClient.invalidateQueries({ queryKey: ["entries"] });
                      void queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
                      void queryClient.invalidateQueries({ queryKey: ["entries-count"] });
                      void queryClient.invalidateQueries({ queryKey: ["recent-entries-count"] });
                    }}
                  />
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setRecentPage((p) => Math.max(0, p - 1))} disabled={recentPage === 0}>
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {recentPage + 1} of {Math.max(1, Math.ceil((recentCountData?.count ?? 0) / recentPageSize))}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => setRecentPage((p) => p + 1)} disabled={myRecentEntries.length < recentPageSize}>
                        Next
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Show</span>
                      <Select value={String(recentPageSize)} onValueChange={(v) => { setRecentPageSize(Number(v)); setRecentPage(0); }}>
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 10, 20, 50].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground">per page</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

      <Dialog open={fieldNotesDialogOpen} onOpenChange={setFieldNotesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{fieldNotesEntry?.field_notes ? "Edit note" : "Add note"}</DialogTitle>
            <DialogDescription>
              Punch on {fieldNotesEntry ? (() => { const p = fmtPunchTime(fieldNotesEntry.recorded_time, fieldNotesEntry.tz_name); return p.primary + (p.secondary ? " / " + p.secondary : ""); })() : ""} — {fieldNotesEntry ? displayPunchActionLabel(displayPunchAction(fieldNotesEntry)).toLowerCase() : ""}. Saved on this punch and included in QuickBase audit notes after sync. Use the Field Notes tab for formal corrections.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={fieldNotesText}
            onChange={(e) => setFieldNotesText(e.target.value)}
            placeholder="Additional note for this punch (shown in View notes; synced to QB audit notes)…"
            rows={4}
            className="min-w-0 max-w-full break-words [overflow-wrap:anywhere]"
          />
          {updateFieldNotesMutation.isError && (
            <div className="text-xs text-destructive">
              {updateFieldNotesMutation.error instanceof ApiError
                ? updateFieldNotesMutation.error.message
                : "Update failed."}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFieldNotesDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={updateFieldNotesMutation.isPending}
              onClick={() => {
                if (fieldNotesEntry) {
                  updateFieldNotesMutation.mutate({ id: fieldNotesEntry.id, field_notes: fieldNotesText || null });
                }
              }}
            >
              {updateFieldNotesMutation.isPending ? "Saving…" : "Save Notes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
